<template>
  <div class="match-game">
    <h1>Match the Body Parts</h1>

    <div
      v-if="!gameCompleted"
      class="game-container"
    >
      <!-- Display the current illustration -->
      <div class="illustration-container">
        <!-- Top Part with Arrow Buttons -->
        <div class="navigation-buttons">
          <button @click="prevTopPart">
            ⬅️
          </button>
          <button @click="nextTopPart">
            ➡️
          </button>
        </div>
        <div class="illustration-part">
          <div
            class="image-part top-part"
            :style="topPartStyle"
          />
        </div>

        <!-- Middle Part (Fixed) -->
        <div class="illustration-part">
          <div
            class="image-part middle-part"
            :style="middlePartStyle"
          />
        </div>

        <div class="illustration-part">
          <div
            class="image-part bottom-part"
            :style="bottomPartStyle"
          />
        </div>

        <!-- Bottom Part with Arrow Buttons -->
        <div class="navigation-buttons">
          <button @click="prevBottomPart">
            ⬅️
          </button>
          <button @click="nextBottomPart">
            ➡️
          </button>
        </div>
      </div>

      <!-- Message Display -->
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
    </div>

    <!-- Game Completed Message -->
    <div v-else>
      <LevelComplete />
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      animals: [
        {
          name: "Lion",
          image:
            "https://as2.ftcdn.net/v2/jpg/01/64/70/45/1000_F_164704501_ut2tFJ12b9jdMWiMbzmrPfdv580TiB0S.jpg",
        },
        {
          name: "Kid",
          image:
            "https://img.freepik.com/premium-vector/cute-kid-illustration_961307-6504.jpg",
        },
        {
          name: "Elephant",
          image:
            "https://www.shutterstock.com/image-vector/young-cute-elephant-baby-vector-600nw-2270842531.jpg",
        },
        {
          name: "Giraffe",
          image:
            "https://as2.ftcdn.net/v2/jpg/02/87/58/89/1000_F_287588969_uMK4takhaHbxgZB2lVVAu3VTADzBQ5pB.jpg",
        },
        // Add more animals as needed
      ],
      currentIllustrationIndex: 0,
      topPartIndices: [],
      bottomPartIndices: [],
      topPartIndex: 0,
      bottomPartIndex: 0,
      message: "",
      gameCompleted: false,
      selectedVoice: null,
    };
  },
  computed: {
    topPartStyle() {
      const index = this.topPartIndices[this.topPartIndex];
      return this.getPartStyle(this.animals[index].image, "top");
    },
    middlePartStyle() {
      return this.getPartStyle(this.currentIllustration.image, "middle");
    },
    bottomPartStyle() {
      const index = this.bottomPartIndices[this.bottomPartIndex];
      return this.getPartStyle(this.animals[index].image, "bottom");
    },
    currentIllustration() {
      return this.animals[this.currentIllustrationIndex];
    },
  },
  created() {
    this.setupGame();
  },
  mounted() {
    this.setupVoices();
  },
  methods: {
    setupGame() {
      this.gameCompleted = false;
      this.message = "";
      this.currentIllustrationIndex = 0;
      this.loadParts();
    },
    resetGame() {
      this.setupGame();
    },
    loadParts() {
      const indices = [...Array(this.animals.length).keys()];
      this.topPartIndices = this.shuffleArray([...indices]);
      this.bottomPartIndices = this.shuffleArray([...indices]);
      this.topPartIndex = 0;
      this.bottomPartIndex = 0;
    },
    nextTopPart() {
      this.topPartIndex = (this.topPartIndex + 1) % this.topPartIndices.length;
      this.checkMatch();
    },
    prevTopPart() {
      this.topPartIndex =
        (this.topPartIndex - 1 + this.topPartIndices.length) %
        this.topPartIndices.length;
      this.checkMatch();
    },
    nextBottomPart() {
      this.bottomPartIndex =
        (this.bottomPartIndex + 1) % this.bottomPartIndices.length;
      this.checkMatch();
    },
    prevBottomPart() {
      this.bottomPartIndex =
        (this.bottomPartIndex - 1 + this.bottomPartIndices.length) %
        this.bottomPartIndices.length;
      this.checkMatch();
    },
    checkMatch() {
      const currentTopIndex = this.topPartIndices[this.topPartIndex];
      const currentBottomIndex = this.bottomPartIndices[this.bottomPartIndex];
      const correctIndex = this.currentIllustrationIndex;

      if (
        currentTopIndex === correctIndex &&
        currentBottomIndex === correctIndex
      ) {
        // Correct match
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );
        this.speakOutLoud(`${this.currentIllustration.name}!`);

        // Move to the next illustration after a short delay
        setTimeout(() => {
          this.currentIllustrationIndex++;
          if (this.currentIllustrationIndex >= this.animals.length) {
            // Game completed
            this.gameCompleted = true;

            setTimeout(() => {
              this.$router.push("/body"); // Adjust the route as necessary
            }, 4000);
          } else {
            // Load new parts
            this.loadParts();
          }
        }, 2000);
      } else {
        // Incorrect match
        this.message = "Keep trying!";
        // Optionally, play an error sound
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-button.mp3"
        );
        // Clear message after 2 seconds
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
    },
    shuffleArray(array) {
      let currentIndex = array.length,
        temporaryValue,
        randomIndex;

      while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;

        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }

      return array;
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play().catch((e) =>
        console.error(`Error playing sound from ${url}:`, e)
      );
    },
    getPartStyle(imageUrl, part) {
      // Ensure images are of consistent dimensions for accurate results
      return {
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: "300px 300px", // Adjust to your image dimensions
        backgroundPosition: this.getBackgroundPosition(part),
        height: "100px",
        width: "300px",
      };
    },
    getBackgroundPosition(part) {
      switch (part) {
        case "top":
          return "center top";
        case "middle":
          return "center center";
        case "bottom":
          return "center bottom";
        default:
          return "center center";
      }
    },
  },
};
</script>

<style scoped>
.match-game {
  text-align: center;
  width: 100%;
}

.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.illustration-container {
  width: 350px;
  margin-bottom: 20px;
}

.illustration-part {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
}

.image-part {
  width: 300px;
  height: 100px; /* Adjusted height for each part */
  overflow: hidden;
  background-repeat: no-repeat;
}

.navigation-buttons {
  display: flex;
  justify-content: center;
  margin-bottom: 5px;
}

.message {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
}

button {
  padding: 5px 10px;
  font-size: 1rem;
  cursor: pointer;
  margin: 0 5px;
}
</style>